import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { getObjectTypeName } from "../../common/utilities/GetObjectTypeName";
import { RouteFromObjectType } from "../../common/utilities/RouteFromObjectType";
import UUID from "../../common/utilities/Uuid";
import restService from "../rest-service/RestService";
import { AttributeSchemaTypeEnum, IAttribute, IAttributeModel, IAttributeSchemaModel, IAttributeUsage, IObjectAttributes } from "./Models";


type GetCustomerAttributeQueryParams = {
    contextGroupID: number
};

type AttributeSchemaQueryParams = {
    groupID: number,
    id?: number,
    attributeName?: string,
    name?: string
};


type SaveAttributesType = {
    attributes: IAttribute[];
    createMissingSchemas: boolean;
}

const AttributeDataService = {

    getAttributes: async function(contextGroupID: number, eLynxObject: IeLynxObject, signal?: AbortSignal): Promise<IAttribute[]> {

        const path =`/groups/${contextGroupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/attributes`;
        
        const response = await restService.get<IAttribute[]>(path, signal);

        return response.data;
    },
    getCustomerAttributes: async function(contextGroupID: number, signal?: AbortSignal): Promise<IAttributeModel[]> {

        const path = 'customer/attributes';
        
        const response = await restService.get<IAttributeModel[], GetCustomerAttributeQueryParams>(path, signal, {contextGroupID: contextGroupID});

        return response.data;
    },
    getCustomerAttribute: async function(contextGroupID: number, name: string, signal?: AbortSignal): Promise<IAttributeModel> {
        const path = `customer/attributes/${name}`;
        
        const response = await restService.get<IAttributeModel, GetCustomerAttributeQueryParams>(path, 
                                    signal, 
                                    {
                                        contextGroupID: contextGroupID
                                    });

        return response.data;
    },
    getDeviceAttributes: async function(groupID: number, signal?: AbortSignal): Promise<IAttributeModel[]> {

        const path = `groups/${groupID}/devices/attributes`;
       
        const response = await restService.get<IAttributeModel[]>(path, signal);
        return response.data;
    },
    getObjectAttributes: async function(groupID: number, objectType: eLynxObjectTypeEnum, signal?: AbortSignal): Promise<IAttributeModel[]> {

        const path = `groups/${groupID}/${getObjectTypeName(objectType, true, true)}/attributes`;
       
        const response = await restService.get<IAttributeModel[]>(path, signal);
        return response.data.sort((a, b) => a.attributeName.toLowerCase().localeCompare(b.attributeName.toLowerCase()));
    },
    getTankAttributes: async function(groupID: number, signal?: AbortSignal): Promise<IAttributeModel[]> {

        const path = `groups/${groupID}/tanks/attributes`;
       
        const response = await restService.get<IAttributeModel[]>(path, signal);
        return response.data;
    },

    getAttributesUsage: async function(organizationID: number, groupID: number, attributeName?: string, signal?: AbortSignal): Promise<IAttributeUsage[]> {

        const path = `organizations/${organizationID}/attributes/usage`;
        const params: AttributeSchemaQueryParams = {
            groupID,
            attributeName
        };
       
        const response = await restService.get<IAttributeUsage[], AttributeSchemaQueryParams>(path, signal, params);
        return response.data;
    },
    getAttributeSchemas:async function(organizationID: number, groupID: number, signal?: AbortSignal): Promise<IAttributeSchemaModel[]> {

        const path = `organizations/${organizationID}/attribute-management/attribute-schemas`;

        const params: AttributeSchemaQueryParams = {
            groupID
        }
    
        const response = await restService.get<IAttributeSchemaModel[], AttributeSchemaQueryParams>(path, signal, params);
        return response.data;

    },
    getAttributeSchema: async function(organizationID: number, groupID: number, id: number, name: string, signal?: AbortSignal): Promise<IAttributeSchemaModel | undefined> {

        if (id > 0 || name !== '') {
            const path = `organizations/${organizationID}/attribute-management/attribute-schemas`;
    
            const params: AttributeSchemaQueryParams = {
                groupID,
                //id,
                name: name
            }
        
            const response = await restService.get<IAttributeSchemaModel[], AttributeSchemaQueryParams>(path, signal, params);
            return response.data.length ? response.data[0] : undefined;
        } else {
            return {

                id: id,
                decimalDigits: 0,
                name: '',
                schemaType: AttributeSchemaTypeEnum.None,
                valueOptions: [],
                wholeDigits: 99,
                allowGroupBy: false
            } as IAttributeSchemaModel;
        }

    },

    getObjectsAttributeValues: async function(organizationID: number, groupID: number, attributeName: string, signal?: AbortSignal) : Promise<IObjectAttributes> {

        const path = `organizations/${organizationID}/attribute-management/object-attribute-values`;
    
        // the strange way the backend works requires a name (not empty string) to return list of available objects
        // otherwise, it returns every attribute for every object in the group
        const name = attributeName !== '' ? attributeName : UUID.generate();
        const params: AttributeSchemaQueryParams = {
            groupID,
            attributeName: name
        };
    
        const response = await restService.get<IObjectAttributes, AttributeSchemaQueryParams>(path, signal, params);
        const data = response.data;

        data.devices.forEach(o => o.oselected = o.selected);
        data.wells.forEach(o => o.oselected = o.selected);
        data.tanks.forEach(o => o.oselected = o.selected);
        data.tankBatteries.forEach(o => o.oselected = o.selected);

        return data;
    },
    saveAttributeSchema: async function(organizationID: number,
        groupID: number,
        schema: IAttributeSchemaModel): Promise<IAttributeSchemaModel> {


        const path = `organizations/${organizationID}/attribute-management/attribute-schema`;
    
        const params: AttributeSchemaQueryParams = {
                groupID,
        };

        const response = await restService.put<IAttributeSchemaModel, AttributeSchemaQueryParams, IAttributeSchemaModel>(path, schema, undefined, params);
        return response.data;
    },
    saveAttributes: async function(organizationID: number,
        groupID: number,
        attributes: IAttribute[]): Promise<void> {


        const path = `organizations/${organizationID}/attributes`;
    
        const params: AttributeSchemaQueryParams = {
                groupID,
        };

        await restService.post<SaveAttributesType, void, AttributeSchemaQueryParams>(path, {
            attributes,
            createMissingSchemas: false
        }, undefined, undefined, params);
    },
    deleteAttributeSchema: async function(organizationID: number, 
        groupID: number,
        schemaID: number): Promise<IAttributeSchemaModel> {

        const path = `organizations/${organizationID}/attribute-management/attribute-schema/${schemaID}`;

        const params: AttributeSchemaQueryParams = {
                groupID,

        };

        const response = await restService.delete<IAttributeSchemaModel, AttributeSchemaQueryParams>(path, undefined, params);
        return response.data;
    },
    deleteAttributes: async function(contextGroupID: number, eLynxObject: IeLynxObject, attributes: IAttributeModel[]): Promise<void> {

        const path =`/groups/${contextGroupID}/${RouteFromObjectType(eLynxObject)}/${eLynxObject.id}/attributes`;
        
        await restService.delete<void, IAttributeModel[]>(path, undefined, attributes);
    },


};

export default AttributeDataService;